import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from '../api';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ProductForm from '../components/ProductForm';
import SellProduct from '../components/SellProduct';
import ProductList from '../components/ProductList';
import { useUser } from '../components/UserContext';
import { useToast } from '../components/ToastProvider';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx } from '../styles/tokens';

const VALID_STATUS = new Set(['all', 'active', 'inactive']);

const Home = () => {
  const { user } = useUser();
  const showToast = useToast();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const searchTerm = searchParams.get('q') || '';
  const statusFilter = VALID_STATUS.has(searchParams.get('status')) ? searchParams.get('status') : 'all';
  const categoryFilter = searchParams.get('category') || 'all';

  const updateListParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        const clearDefault =
          (key === 'q' && !value) ||
          (key === 'status' && (!value || value === 'all')) ||
          (key === 'category' && (!value || value === 'all'));
        if (clearDefault) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setSearchTerm = (value) => updateListParams({ q: value });
  const setStatusFilter = (value) => updateListParams({ status: value });
  const setCategoryFilter = (value) => updateListParams({ category: value });

  const isWarehouse = user?.role === 'warehouse';
  const listReturnPath = `${location.pathname}${location.search}`;

  const handleToggleActive = async (product) => {
    const newActive = product.is_active !== false;
    try {
      await axios.put(`/api/products/${product.id}`, {
        is_active: !newActive,
      });
      await fetchProducts();
      showToast(!newActive ? 'Product marked as inactive' : 'Product marked as active', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update product status', 'error');
    }
  };

  const fetchProducts = async (status = statusFilter) => {
    setLoading(true);
    try {
      const params = status !== 'all' ? { status } : {};
      const response = await axios.get('/api/products', { params });
      setProducts(response.data);
    } catch (err) {
      showToast('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleAddProductModal = (product = null) => {
    setEditingProduct(product);
    setModalOpen(!modalOpen);
  };

  const toggleSellProductModal = () => {
    setSellModalOpen(!sellModalOpen);
  };

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const filteredProducts = products.filter((product) => {
    const search = searchTerm.toLowerCase();
    const name = (product.name || '').toLowerCase();
    const category = (product.category || '').toLowerCase();
    const productCode = (product.product_code || '').toLowerCase();
    const matchesSearch = name.includes(search) || category.includes(search) || productCode.includes(search);
    const matchesCategory = categoryFilter === 'all' || (product.category || '') === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    fetchProducts(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  if (loading) {
    return <PageLoader message="Loading products..." />;
  }

  return (
    <Box sx={pageContainerSx}>
      <PageHeader
        title="Products"
        subtitle="Manage your product inventory"
        actionLabel={!isWarehouse ? 'Add Product' : undefined}
        actionIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onAction={!isWarehouse ? () => toggleAddProductModal() : undefined}
      />

      {/* Filters: Status, Category, Search */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 1.25 }}>
        <FormControl size="small" sx={{ width: { xs: '100%', sm: 128 }, height: 32 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            renderValue={(v) => (v === 'all' ? 'All Status' : v === 'active' ? 'Active' : 'Inactive')}
            sx={{
              height: 32,
              fontSize: '0.75rem',
              borderRadius: 1,
              '& .MuiSelect-select': { py: 0.75 },
              '& fieldset': { borderColor: 'divider' },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Status</MenuItem>
            <MenuItem value="active" sx={{ fontSize: '0.75rem' }}>Active</MenuItem>
            <MenuItem value="inactive" sx={{ fontSize: '0.75rem' }}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: { xs: '100%', sm: 128 }, height: 32 }}>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            displayEmpty
            renderValue={(v) => (v === 'all' ? 'All Category' : v)}
            sx={{
              height: 32,
              fontSize: '0.75rem',
              borderRadius: 1,
              '& .MuiSelect-select': { py: 0.75 },
              '& fieldset': { borderColor: 'divider' },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Category</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat} sx={{ fontSize: '0.75rem' }}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder="Search products..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': {
              height: 32,
              fontSize: '0.75rem',
              borderRadius: 1,
              '& fieldset': { borderColor: 'divider' },
              '& input': { py: 0.75 },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table card */}
      <ProductList
          products={filteredProducts}
          onEdit={toggleAddProductModal}
          onToggleActive={!isWarehouse ? handleToggleActive : undefined}
          viewOnly={isWarehouse}
          listReturnPath={listReturnPath}
        />

      {modalOpen && (
        <ProductForm
          onAdd={fetchProducts}
          closeModal={() => toggleAddProductModal(null)}
          editingProduct={editingProduct}
        />
      )}
      {!isWarehouse && sellModalOpen && (
        <SellProduct onSell={fetchProducts} closeModal={toggleSellProductModal} />
      )}
    </Box>
  );
};

export default Home;
